import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ClientService } from "../client.service";
import { ClientDocumentAddDialogComponent } from "../client-document-add-dialog/client-document-add-dialog.component";
import {
  BILLING_MODES,
  CLIENT_PLAN_OPTIONS,
  ClientDocumentMeta,
  ClientNote,
  ClientWithStats,
  DEFAULT_OPTIONAL_INTERFACES,
  EditClientForm,
  EditClientRequest,
  LINK_PREVIEW_MODES,
  NAME_PRONUNCIATION_ROLE_OPTIONS,
  NAME_PRONUNCIATION_SCOPES,
  OptionalInterfaces,
  OptionalInterfacesForm,
  type BillingMode,
  type ClientPlanId,
  type NamePronunciationRole,
} from "../client.interface";
import { validateFutureDate } from "../../shared/validators/date.validator";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { ToastrService } from "../../shared/toastr.service";
import { AuthService } from "../../auth/auth.service";

@Component({
  selector: "app-client-detail",
  templateUrl: "./client-detail.component.html",
  styleUrls: ["./client-detail.component.scss"],
  standalone: false,
})
export class ClientDetailComponent implements OnInit, OnDestroy {
  form: FormGroup<EditClientForm> | null = null;
  client: ClientWithStats | null = null;
  notes: ClientNote[] = [];
  documents: ClientDocumentMeta[] = [];
  loading = true;
  saving = false;
  noteSaving = false;
  editingNoteId: string | null = null;
  noteDraft = "";
  newNoteBody = "";
  oldDomain = "";
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  readonly subscriptions = CLIENT_PLAN_OPTIONS;
  readonly linkPreviewModes = LINK_PREVIEW_MODES;
  readonly namePronunciationScopes = NAME_PRONUNCIATION_SCOPES;
  readonly namePronunciationRoleOptions = NAME_PRONUNCIATION_ROLE_OPTIONS;
  readonly billingModes = BILLING_MODES;

  private readonly destroy$ = new Subject<void>();
  private readonly formDestroy$ = new Subject<void>();
  private readonly trialFinishDateValidations = [
    (c: FormControl) => Validators.required(c),
    validateFutureDate,
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly clientService: ClientService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const clientId = params.get("clientId");
      if (!clientId) {
        void this.router.navigate(["/admin/client"]);
        return;
      }
      this.loadClient(clientId);
    });
  }

  ngOnDestroy(): void {
    this.formDestroy$.next();
    this.formDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isDomainChanged(): boolean {
    if (!this.form) {
      return false;
    }
    return this.form.controls.domain.value.trim() !== this.oldDomain.trim();
  }

  back(): void {
    void this.router.navigate(["/admin/client"]);
  }

  copyClientId(): void {
    if (!this.client?.clientId) {
      return;
    }
    void navigator.clipboard.writeText(this.client.clientId).then(() => {
      this.toastr.success(
        this.translate.instant("clientDetail.clientIdCopied") as string,
      );
    });
  }

  save(): void {
    if (!this.form || !this.client) {
      return;
    }
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }
    if (
      this.isDomainChanged &&
      !this.form.controls.domainChangeAcknowledged.value
    ) {
      return;
    }
    const raw = this.form.getRawValue();
    if (raw.planId == null) {
      return;
    }
    this.saving = true;
    const value: EditClientRequest = {
      domain: raw.domain,
      googleAdminEmail: raw.googleAdminEmail ?? "",
      planId: raw.planId,
      isIncludedDemoContent: false,
      trialFinishDate: raw.trialFinishDate,
      excludedNewEmployees: raw.excludedNewEmployees,
      language: raw.language,
      billingMode: raw.billingMode,
      optionalInterfaces: {
        digitalCard: raw.optionalInterfaces.digitalCard,
        mobileApps: raw.optionalInterfaces.mobileApps,
        browserExtension: raw.optionalInterfaces.browserExtension,
        appleWallet: raw.optionalInterfaces.appleWallet,
        googleWallet: raw.optionalInterfaces.googleWallet,
        emailSignatures: raw.optionalInterfaces.emailSignatures,
        virtualBackgrounds: raw.optionalInterfaces.virtualBackgrounds,
        linkPreviewMode: raw.optionalInterfaces.linkPreviewMode,
        selfPrintCards: raw.optionalInterfaces.selfPrintCards,
        contextualQr: raw.optionalInterfaces.contextualQr,
        namePronunciation: raw.optionalInterfaces.namePronunciation,
        namePronunciationScope: raw.optionalInterfaces.namePronunciationScope,
        namePronunciationRoles:
          raw.optionalInterfaces.namePronunciationScope === "designated_roles"
            ? raw.optionalInterfaces.namePronunciationRoles
            : [],
      },
    };

    this.clientService
      .edit(this.client.clientId, value)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant("editClient.success") as string,
          );
          if (this.auth.currentDomain === this.oldDomain) {
            void this.auth.fetchCurrent();
          }
          this.loadClient(this.client.clientId);
        },
      });
  }

  addNote(): void {
    if (!this.client || !this.newNoteBody.trim() || this.noteSaving) {
      return;
    }
    this.noteSaving = true;
    this.clientService
      .createNote(this.client.clientId, this.newNoteBody.trim())
      .pipe(
        finalize(() => {
          this.noteSaving = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (note) => {
          this.notes = [note, ...this.notes];
          this.newNoteBody = "";
          this.toastr.success(
            this.translate.instant("clientDetail.notes.added") as string,
          );
        },
      });
  }

  startEditNote(note: ClientNote): void {
    this.editingNoteId = note.clientNoteId;
    this.noteDraft = note.body;
  }

  cancelEditNote(): void {
    this.editingNoteId = null;
    this.noteDraft = "";
  }

  saveNote(note: ClientNote): void {
    if (!this.client || !this.noteDraft.trim() || this.noteSaving) {
      return;
    }
    this.noteSaving = true;
    this.clientService
      .updateNote(
        this.client.clientId,
        note.clientNoteId,
        this.noteDraft.trim(),
      )
      .pipe(
        finalize(() => {
          this.noteSaving = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (updated) => {
          this.notes = this.notes.map((item) =>
            item.clientNoteId === updated.clientNoteId ? updated : item,
          );
          this.cancelEditNote();
          this.toastr.success(
            this.translate.instant("clientDetail.notes.updated") as string,
          );
        },
      });
  }

  deleteNote(note: ClientNote): void {
    if (!this.client) {
      return;
    }
    this.clientService
      .deleteNote(this.client.clientId, note.clientNoteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notes = this.notes.filter(
            (item) => item.clientNoteId !== note.clientNoteId,
          );
          if (this.editingNoteId === note.clientNoteId) {
            this.cancelEditNote();
          }
          this.toastr.success(
            this.translate.instant("clientDetail.notes.deleted") as string,
          );
        },
      });
  }

  openAddDocumentDialog(): void {
    if (!this.client) {
      return;
    }
    this.dialog
      .open(ClientDocumentAddDialogComponent, {
        width: "600px",
        maxWidth: "92vw",
        maxHeight: "calc(100vh - 48px)",
        panelClass: "app-dialog-panel",
        data: { clientId: this.client.clientId },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((document: ClientDocumentMeta | undefined) => {
        if (document) {
          this.documents = [document, ...this.documents];
        }
      });
  }

  downloadDocument(doc: ClientDocumentMeta): void {
    if (!this.client) {
      return;
    }
    this.clientService
      .downloadDocument(this.client.clientId, doc.clientDocumentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = doc.fileName;
          anchor.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  deleteDocument(doc: ClientDocumentMeta): void {
    if (!this.client) {
      return;
    }
    this.clientService
      .deleteDocument(this.client.clientId, doc.clientDocumentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.documents = this.documents.filter(
            (item) => item.clientDocumentId !== doc.clientDocumentId,
          );
          this.toastr.success(
            this.translate.instant("clientDetail.documents.deleted") as string,
          );
        },
      });
  }

  formatBytes(size: number): string {
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  private loadClient(clientId: string): void {
    this.loading = true;
    this.client = null;
    this.form = null;
    this.notes = [];
    this.documents = [];
    this.editingNoteId = null;
    this.noteDraft = "";
    this.newNoteBody = "";

    this.clientService
      .getOne(clientId)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (client) => {
          this.client = client;
          this.oldDomain = client.domain;
          this.notes = client.notes ?? [];
          this.documents = client.documents ?? [];
          this.buildForm(client);
        },
        error: () => {
          void this.router.navigate(["/admin/client"]);
        },
      });
  }

  private buildForm(data: ClientWithStats): void {
    this.formDestroy$.next();
    const oi: OptionalInterfaces =
      data.optionalInterfaces ?? DEFAULT_OPTIONAL_INTERFACES;
    const billingMode: BillingMode = data.billingMode ?? "default";
    const planId: ClientPlanId | null =
      data.planId === "free" ||
      data.planId === "freemium" ||
      data.planId === "paid" ||
      data.planId === "partner" ||
      data.planId === "reseller"
        ? data.planId
        : null;

    this.form = new FormGroup<EditClientForm>({
      domain: new FormControl(data.domain, {
        nonNullable: true,
        validators: [(c) => Validators.required(c)],
      }),
      domainChangeAcknowledged: new FormControl(false, { nonNullable: true }),
      googleAdminEmail: new FormControl(data.googleAdminEmail ?? null, [
        (c) => Validators.email(c),
      ]),
      planId: new FormControl<ClientPlanId | null>(planId, [
        (c) => Validators.required(c),
      ]),
      trialFinishDate: new FormControl(
        data.trialFinishDate ? new Date(data.trialFinishDate) : null,
        planId === "free" ? this.trialFinishDateValidations : [],
      ),
      excludedNewEmployees: new FormControl(
        data.excludedNewEmployees || false,
        { nonNullable: true },
      ),
      language: new FormControl(data.language || "en", {
        nonNullable: true,
        validators: [(c) => Validators.required(c)],
      }),
      billingMode: new FormControl<BillingMode>(billingMode, {
        nonNullable: true,
        validators: [(c) => Validators.required(c)],
      }),
      optionalInterfaces: new FormGroup<OptionalInterfacesForm>({
        digitalCard: new FormControl(oi.digitalCard, { nonNullable: true }),
        mobileApps: new FormControl(oi.mobileApps, { nonNullable: true }),
        browserExtension: new FormControl(oi.browserExtension, {
          nonNullable: true,
        }),
        appleWallet: new FormControl(oi.appleWallet, { nonNullable: true }),
        googleWallet: new FormControl(oi.googleWallet, { nonNullable: true }),
        emailSignatures: new FormControl(oi.emailSignatures, {
          nonNullable: true,
        }),
        virtualBackgrounds: new FormControl(oi.virtualBackgrounds, {
          nonNullable: true,
        }),
        linkPreviewMode: new FormControl(oi.linkPreviewMode, {
          nonNullable: true,
        }),
        selfPrintCards: new FormControl(oi.selfPrintCards, {
          nonNullable: true,
        }),
        contextualQr: new FormControl(oi.contextualQr, { nonNullable: true }),
        namePronunciation: new FormControl(oi.namePronunciation, {
          nonNullable: true,
        }),
        namePronunciationScope: new FormControl(oi.namePronunciationScope, {
          nonNullable: true,
        }),
        namePronunciationRoles: new FormControl<NamePronunciationRole[]>(
          oi.namePronunciationRoles.filter(
            (role): role is NamePronunciationRole =>
              (NAME_PRONUNCIATION_ROLE_OPTIONS as readonly string[]).includes(
                role,
              ),
          ),
          { nonNullable: true },
        ),
      }),
    });

    this.form.controls.domain.valueChanges
      .pipe(takeUntil(this.formDestroy$), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isDomainChanged) {
          this.form?.controls.domainChangeAcknowledged.setValue(false);
        }
      });

    this.form.controls.planId.valueChanges
      .pipe(takeUntil(this.formDestroy$), takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!this.form) {
          return;
        }
        if (value === "free") {
          this.form.controls.trialFinishDate.setValidators(
            this.trialFinishDateValidations,
          );
        } else {
          this.form.controls.trialFinishDate.clearValidators();
          this.form.controls.trialFinishDate.setValue(null);
        }
        this.form.controls.trialFinishDate.updateValueAndValidity();
      });
  }
}
